// src/pages/boom-sprayer/monitor-rencana-boom/MonitorRencanaBoom.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, push, update } from "firebase/database";

// Import XLSX untuk handling file Excel
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    excelDateToDDMMYYYY,
    formatNumber,
    formatDecimal,
    formatText
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import CustomTableContainer dan CustomBreadcrumbs dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

import SummaryBoom from "../summary-boom/SummaryBoom";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Ambil karakter ke-6 dari bibit untuk menentukan Kelas Bibit
const getKelasBibit = (bibit) => {
    if (!bibit || bibit === '') {
        return '-'; // Ubah dari 'Ratoon' menjadi '-'
    }
    
    const bibitString = String(bibit);
    if (bibitString.length >= 7) {
        const seventhChar = bibitString.charAt(6); // Index 6 = karakter ke-7
        // Hanya kembalikan karakter ke-7 saja, tanpa mapping
        return seventhChar;
    }
    return '-'; // Jika kurang dari 7 karakter, tampilkan '-'
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find(lokasi => lokasi.id_lokasi === id_lokasi);
};

// Fungsi untuk import Excel ke Firebase untuk rencana boom spraying
const importExcelToFirebaseBoom = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // Gunakan raw: true untuk mendapatkan nilai mentah
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1, 
                    raw: false, // Ganti menjadi false untuk mendapatkan nilai hasil formula
                    defval: '' 
                });
                
                // Debug: log struktur data Excel
                console.log("Data Excel raw (10 baris pertama):");
                for (let i = 0; i < Math.min(10, jsonData.length); i++) {
                    console.log(`Row ${i}:`, jsonData[i]);
                }
                
                const dataStartRow = 4; // B6 (0-based index: 5)
                
                // Column mapping yang baru dengan penambahan kolom luasan (Tambahkan kondisi untuk memeriksa id_lokasi, aktifitas, rencana_bulan_ini, real_bulan_ini)
                const columnMapping = {
                    id_lokasi: 0, // Kolom B
                    luasan: 3,     // Kolom E
                    aktifitas: 4, // Kolom F
                    real_bulan_lalu: 5, // Kolom G
                    rencana_bulan_ini: 6, // Kolom H
                    real_bulan_ini: 7, // Kolom I
                    interval_rencana: 8, // Kolom J
                    interval_real: 9 // Kolom K
                };

                console.log("Sample raw data dari Excel (baris 6):");
                if (jsonData.length > dataStartRow) {
                    const sampleRow = jsonData[dataStartRow];
                    console.log("Row 6 raw:", sampleRow);
                    console.log("id_lokasi:", sampleRow[columnMapping.id_lokasi], "type:", typeof sampleRow[columnMapping.id_lokasi]);
                    console.log("luasan:", sampleRow[columnMapping.luasan], "type:", typeof sampleRow[columnMapping.luasan]);
                    console.log("aktifitas:", sampleRow[columnMapping.aktifitas], "type:", typeof sampleRow[columnMapping.aktifitas]);
                    console.log("real_bulan_lalu:", sampleRow[columnMapping.real_bulan_lalu], "type:", typeof sampleRow[columnMapping.real_bulan_lalu]);
                }

                const boomRef = ref(database, "db_boom_spraying/tb_rencana_boom_spraying");
                const existingBoomSnapshot = await onValuePromise(boomRef);
                const existingBoomData = existingBoomSnapshot.val() || {};

                const existingBoomMap = new Map();
                Object.keys(existingBoomData).forEach(fbKey => {
                    const record = existingBoomData[fbKey];
                    if (record.id_lokasi && record.aktifitas) {
                        const key = `${record.id_lokasi.toString().trim()}_${record.aktifitas.toString().trim()}`;
                        existingBoomMap.set(key, fbKey);
                    }
                });

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;

                // Proses data
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const id_lokasi = row[columnMapping.id_lokasi];
                    const luasan = row[columnMapping.luasan];
                    const aktifitas = row[columnMapping.aktifitas];
                    const real_bulan_lalu = row[columnMapping.real_bulan_lalu];
                    const rencana_bulan_ini = row[columnMapping.rencana_bulan_ini];
                    const real_bulan_ini = row[columnMapping.real_bulan_ini];
                    const interval_rencana = row[columnMapping.interval_rencana];
                    const interval_real = row[columnMapping.interval_real];
                    
                    // Skip jika id_lokasi kosong atau aktifitas kosong
                    if (!id_lokasi || (typeof id_lokasi === 'string' && id_lokasi.trim() === '')) {
                        ignoredCount++;
                        continue;
                    }

                    const formattedIdLokasi = String(id_lokasi).trim();
                    const formattedAktifitas = String(aktifitas).trim();
                    
                    // Format luasan
                    let formattedLuasan;
                    if (luasan === undefined || luasan === null || 
                        (typeof luasan === 'string' && luasan.trim() === '')) {
                        formattedLuasan = '';
                    } else {
                        formattedLuasan = String(luasan).trim();
                    }
                    
                    // Format tanggal menggunakan excelDateToDDMMYYYY
                    const formattedRealBulanLalu = excelDateToDDMMYYYY(real_bulan_lalu);
                    const formattedRencanaBulanIni = excelDateToDDMMYYYY(rencana_bulan_ini);
                    const formattedRealBulanIni = excelDateToDDMMYYYY(real_bulan_ini);
                    
                    // Format interval_rencana dan interval_real
                    let formattedIntervalRencana;
                    if (interval_rencana === undefined || interval_rencana === null || 
                        (typeof interval_rencana === 'string' && interval_rencana.trim() === '')) {
                        formattedIntervalRencana = '';
                    } else {
                        formattedIntervalRencana = String(interval_rencana).trim();
                    }
                    
                    let formattedIntervalReal;
                    if (interval_real === undefined || interval_real === null || 
                        (typeof interval_real === 'string' && interval_real.trim() === '')) {
                        formattedIntervalReal = '';
                    } else {
                        formattedIntervalReal = String(interval_real).trim();
                    }
                    
                    // Debug log untuk beberapa data pertama
                    if (importedCount + updatedCount < 2) {
                        console.log(`Data baris ${i + 1}:`);
                        console.log(`  id_lokasi: ${formattedIdLokasi}`);
                        console.log(`  luasan: ${formattedLuasan}`);
                        console.log(`  aktifitas: ${formattedAktifitas}`);
                        console.log(`  real_bulan_lalu raw:`, real_bulan_lalu);
                        console.log(`  real_bulan_lalu formatted:`, formattedRealBulanLalu);
                        console.log(`  rencana_bulan_ini raw:`, rencana_bulan_ini);
                        console.log(`  rencana_bulan_ini formatted:`, formattedRencanaBulanIni);
                        console.log(`  interval_real raw:`, interval_real);
                        console.log(`  interval_real formatted:`, formattedIntervalReal);
                    }

                    // Data dari Excel yang akan disimpan di Firebase
                    const excelDataToUpdate = {
                        id_lokasi: formattedIdLokasi,
                        luasan: formattedLuasan,
                        aktifitas: formattedAktifitas,
                        real_bulan_lalu: formattedRealBulanLalu,
                        rencana_bulan_ini: formattedRencanaBulanIni,
                        real_bulan_ini: formattedRealBulanIni,
                        interval_rencana: formattedIntervalRencana,
                        interval_real: formattedIntervalReal,
                        last_imported_at: importTimestamp,
                    };

                    const compositeKey = `${formattedIdLokasi}_${formattedAktifitas}`;
                    const firebaseKey = existingBoomMap.get(compositeKey);

                    try {
                        if (firebaseKey) {
                            // Update data yang sudah ada
                            await update(ref(database, `db_boom_spraying/tb_rencana_boom_spraying/${firebaseKey}`), excelDataToUpdate);
                            updatedCount++;
                        } else {
                            // Buat data baru
                            await push(boomRef, excelDataToUpdate);
                            importedCount++;
                        }
                        
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        ignoredCount++;
                    }
                }

                console.log(`Hasil import: ${importedCount} baru, ${updatedCount} update, ${ignoredCount} abaikan`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    ignoredCount,
                    total: importedCount + updatedCount
                });

            } catch (error) {
                console.error("Gagal impor Excel:", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

// Fungsi untuk refresh data setelah import
const refreshBoomData = async (database, onValuePromise) => {
    const boomRef = ref(database, "db_boom_spraying/tb_rencana_boom_spraying");
    const updatedBoomSnapshot = await onValuePromise(boomRef);
    const updatedBoomData = updatedBoomSnapshot.val() || {};
    
    const updatedLoadedData = [];
    Object.keys(updatedBoomData).forEach(key => {
        const record = updatedBoomData[key];
        updatedLoadedData.push({ 
            firebaseKey: key, 
            id_lokasi: record.id_lokasi || '',
            luasan: record.luasan || '',
            aktifitas: record.aktifitas || '',
            real_bulan_lalu: record.real_bulan_lalu || '',
            rencana_bulan_ini: record.rencana_bulan_ini || '',
            real_bulan_ini: record.real_bulan_ini || '',
            interval_rencana: record.interval_rencana || '',
            interval_real: record.interval_real || '',
            // Kolom lainnya akan diisi dengan data dari sumber lain
            status: '',
            lokasi: record.id_lokasi || '',
            kelas_bibit: '-',
        });
    });
    
    return updatedLoadedData;
};

function MonitorRencanaBoom() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State untuk data konfigurasi dropdown
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [lokasiData, setLokasiData] = useState([]); // Array data dari db_freshpine/tb_lokasi
    
    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filter states
    const [filterStatusLokasi, setFilterStatusLokasi] = useState('');
    const [filterAktifitas, setFilterAktifitas] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_boom_spraying/tb_rencana_boom_spraying
                const boomRef = ref(database, "db_boom_spraying/tb_rencana_boom_spraying");
                const boomSnapshot = await onValuePromise(boomRef);
                const boomData = boomSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (boomData) {
                    Object.keys(boomData).forEach(key => {
                        const record = boomData[key];
                        loadedData.push({ 
                            firebaseKey: key, 
                            id_lokasi: record.id_lokasi || '',
                            luasan: record.luasan || '', // Tambahkan luasan dari Firebase
                            aktifitas: record.aktifitas || '',
                            real_bulan_lalu: record.real_bulan_lalu || '',
                            rencana_bulan_ini: record.rencana_bulan_ini || '',
                            real_bulan_ini: record.real_bulan_ini || '',
                            interval_rencana: record.interval_rencana || '',
                            interval_real: record.interval_real || '',
                        });
                        
                        // Cari waktu update terakhir dari last_imported_at
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Fetch data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();
                
                let lokasiArray = [];
                if (lokasiRawData) {
                    // Konversi dari object Firebase ke array
                    lokasiArray = Object.keys(lokasiRawData).map(key => ({
                        firebaseKey: key,
                        ...lokasiRawData[key]
                    }));
                    setLokasiData(lokasiArray);
                    
                    // Update dataList dengan informasi dari tb_lokasi
                    const updatedDataList = loadedData.map((item) => {
                        const lokasiInfo = findLokasiById(lokasiArray, item.id_lokasi);
                        
                        if (lokasiInfo) {
                            // Jika ditemukan di tb_lokasi
                            const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                            const displayStatus = lokasiInfo.status || '';
                            const rawBibit = lokasiInfo.bibit || '';
                            const displayKelasBibit = getKelasBibit(rawBibit);
                            
                            return {
                                ...item,
                                lokasi: displayLokasi,
                                // luasan sudah diambil dari Firebase (db_boom_spraying)
                                status: displayStatus,
                                kelas_bibit: displayKelasBibit,
                                tahun: lokasiInfo.tahun || '',
                            };
                        } else {
                            // Jika TIDAK ditemukan di tb_lokasi
                            return {
                                ...item,
                                lokasi: '-',
                                // luasan sudah diambil dari Firebase (db_boom_spraying)
                                status: '',
                                kelas_bibit: '-',
                                tahun: '',
                            };
                        }
                    });
                    
                    setOriginalDataList(updatedDataList);
                    setDataList(updatedDataList);
                } else {
                    // Jika tb_lokasi kosong, gunakan data dasar
                    const basicDataList = loadedData.map((item) => ({
                        ...item,
                        lokasi: item.id_lokasi || '',
                        // luasan sudah diambil dari Firebase (db_boom_spraying)
                        status: '',
                        kelas_bibit: '-',
                        tahun: '',
                    }));
                    
                    setOriginalDataList(basicDataList);
                    setDataList(basicDataList);
                    setLokasiData([]);
                }

                // Fetch lookup data for Status Lokasi (untuk warna indicator)
                const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
                const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
                if (statusLokasiConfigData) {
                    const list = Object.keys(statusLokasiConfigData).map(key => ({
                        id: key,
                        nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                        indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                    }));
                    setStatusLokasiList(list);
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.lokasi && item.lokasi.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handle refresh data setelah import
    const refreshDataAfterImport = async () => {
        try {
            const updatedData = await refreshBoomData(database, onValuePromise);
            
            // Update dengan data dari tb_lokasi jika tersedia
            const finalData = updatedData.map((item) => {
                const lokasiInfo = findLokasiById(lokasiData, item.id_lokasi);
                
                if (lokasiInfo) {
                    const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                    const displayStatus = lokasiInfo.status || '';
                    const rawBibit = lokasiInfo.bibit || '';
                    const displayKelasBibit = getKelasBibit(rawBibit);
                    
                    return {
                        ...item,
                        lokasi: displayLokasi,
                        // luasan sudah diambil dari Firebase (db_boom_spraying)
                        status: displayStatus,
                        kelas_bibit: displayKelasBibit,
                        tahun: lokasiInfo.tahun || '',
                    };
                } else {
                    return {
                        ...item,
                        lokasi: '-',
                        // luasan sudah diambil dari Firebase (db_boom_spraying)
                        status: '',
                        kelas_bibit: '-',
                        tahun: '',
                    };
                }
            });
            
            return finalData;
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Gunakan fungsi import khusus untuk boom spraying
            const result = await importExcelToFirebaseBoom(file, database, onValuePromise);
            
            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui.`,
                    severity: "success",
                });
                
                // Set last updated time
                setLastUpdated(new Date());
                
                // Refresh data setelah import berhasil
                const finalData = await refreshDataAfterImport();
                
                setDataList(finalData);
                setOriginalDataList(finalData);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            handleMenuClose();
            setImportLoading(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterStatusLokasi) {
            filtered = filtered.filter(item => item.status === filterStatusLokasi);
        }
        if (filterAktifitas) {
            filtered = filtered.filter(item => item.aktifitas === filterAktifitas);
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterStatusLokasi('');
        setFilterAktifitas('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Get unique aktifitas for filter dropdown
    const aktifitasList = [...new Set(originalDataList.map(item => item.aktifitas))].filter(Boolean).sort();

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/*<SummaryBoom/>*/}

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status Lokasi</InputLabel>
                            <Select
                                value={filterStatusLokasi}
                                label="Status Lokasi"
                                onChange={(e) => setFilterStatusLokasi(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {statusLokasiList.map((status) => (
                                    <MenuItem key={status.id} value={status.nama_status_lokasi}>
                                        {status.nama_status_lokasi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Aktifitas</InputLabel>
                            <Select
                                value={filterAktifitas}
                                label="Aktifitas"
                                onChange={(e) => setFilterAktifitas(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {aktifitasList.map((aktifitas) => (
                                    <MenuItem key={aktifitas} value={aktifitas}>
                                        {aktifitas}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Rencana Boom Spraying
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari berdasarkan lokasi..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="monitor rencana boom table">
                                <TableHead>
                                    <TableRow className="tr">
                                        <TableCell className="ded-tch">Lokasi</TableCell>
                                        <TableCell className="ded-tch">Status</TableCell>
                                        <TableCell className="ded-tch">Kelas<br/>Bibit</TableCell>
                                        <TableCell className="ded-tch">Luasan</TableCell>
                                        <TableCell className="ded-tch">Aktifitas</TableCell>
                                        <TableCell className="ded-tch">Real<br/>Bulan Lalu</TableCell>
                                        <TableCell className="ded-tch">Rencana<br/>Bulan Ini</TableCell>
                                        <TableCell className="ded-tch">Real<br/>Bulan Ini</TableCell>
                                        <TableCell className="ded-tch">Interval<br/>Rencana</TableCell>
                                        <TableCell className="ded-tch">Interval<br/>Real</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => {
                                            // Lookup nama_status_lokasi dan indicator dari config
                                            const statusLokasiItem = statusLokasiList.find(
                                                (item) => item.nama_status_lokasi === row.status
                                            );
                                            const displayStatusLokasi = row.status || '';
                                            const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

                                            return (
                                                <TableRow key={row.firebaseKey}>
                                                    <TableCell>
                                                        <Box>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                                                {formatText(row.lokasi)}
                                                            </div>
                                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                                ID: {row.id_lokasi || '-'}
                                                            </div>
                                                            <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                                KEY: {row.firebaseKey || '-'}
                                                            </div>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {displayStatusLokasi ? (
                                                            <Chip
                                                                label={displayStatusLokasi}
                                                                style={{ 
                                                                    backgroundColor: statusIndicatorColor, 
                                                                    color: theme.palette.getContrastText(statusIndicatorColor) 
                                                                }}
                                                                size="small"
                                                            />
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell>{formatText(row.kelas_bibit)}</TableCell>
                                                    <TableCell>{formatText(row.luasan)}</TableCell>
                                                    <TableCell className="ded-tbtrtc">{formatText(row.aktifitas)}</TableCell>
                                                    <TableCell>{formatText(row.real_bulan_lalu)}</TableCell>
                                                    <TableCell>{formatText(row.rencana_bulan_ini)}</TableCell>
                                                    <TableCell>{formatText(row.real_bulan_ini)}</TableCell>
                                                    <TableCell>{formatText(row.interval_rencana)}</TableCell>
                                                    <TableCell>{formatText(row.interval_real)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>

                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data monitor rencana boom spraying.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MonitorRencanaBoom;